import React, { useMemo, useState } from 'react';
import { Box, Button, CircularProgress } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiService, SyncSelectionCapError } from '../../services/api';
import { useOrgFeatureFlag } from '../../hooks/useOrgFeatureFlags';
import { useToast } from '../../contexts/ToastContext';
import { variantGroupSelectState } from '../../utils/variantGroupSelection';
import SyncCapNotice from './SyncCapNotice';

export interface VariantGroupSelectAllProps {
  productId: string;
  /** Only a product that belongs to a variant group has anything to select. */
  variantGroupId?: string | null;
  /** The integration whose selection the browser is showing, when it shows one. */
  integrationId?: string | null;
  /** The browser's live sync selection; keeps the count current without a refetch. */
  selectedIds?: ReadonlySet<string>;
  /** `chip` sits with the card's tags; `button` is the product details action. */
  variant: 'chip' | 'button';
  /**
   * Hand a plan-cap refusal to the page's own notice. Without it the action
   * shows `SyncCapNotice` itself, under the button.
   */
  onCapError?: (error: SyncSelectionCapError) => void;
}

/**
 * "Select all N variants": adds every selectable sibling of a variant-group
 * product to the store's sync selection through the ordinary add route, so the
 * plan cap applies and a refusal reads like any other (`SyncCapNotice`).
 * Behind `sync_select_variant_group`; flag off, or while the group is
 * unknown, it renders the plain "Has variants" tag and makes no request.
 */
export default function VariantGroupSelectAll({
  productId,
  variantGroupId,
  integrationId,
  selectedIds,
  variant,
  onCapError,
}: VariantGroupSelectAllProps) {
  const { t } = useTranslation() as unknown as { t: (key: string, options?: Record<string, unknown>) => string };
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const { enabled: flagOn } = useOrgFeatureFlag('sync_select_variant_group');
  const active = flagOn && !!variantGroupId;
  const [capError, setCapError] = useState<SyncSelectionCapError | null>(null);

  const groupQuery = useQuery({
    queryKey: ['store-variant-group', productId, integrationId ?? null],
    queryFn: () => apiService.getStoreVariantGroup(productId, integrationId),
    enabled: active,
    staleTime: 60 * 1000,
  });

  const members = groupQuery.data?.members;
  const state = useMemo(
    () => (members ? variantGroupSelectState(members, selectedIds) : null),
    [members, selectedIds],
  );

  const addMutation = useMutation({
    mutationFn: (ids: string[]) => apiService.addStoreProductsToSync(ids, integrationId),
    onSuccess: (_result, ids) => {
      setCapError(null);
      ['store-sync-selection', 'sync-selection', 'storeCatalogCounts', 'store-variant-group'].forEach((key) =>
        queryClient.invalidateQueries({ queryKey: [key] }),
      );
      showToast(t('productBrowser.variantGroup.added', { count: ids.length }), 'success');
    },
    onError: (error) => {
      if (error instanceof SyncSelectionCapError) {
        if (onCapError) onCapError(error);
        else setCapError(error);
        return;
      }
      showToast(
        error instanceof Error && error.message ? error.message : t('productBrowser.variantGroup.failed'),
        'error',
      );
    },
  });

  if (!flagOn || !variantGroupId) return null;

  const hasVariantsTag = (
    <Box
      sx={{ display: 'inline-flex', alignItems: 'center', height: 20, px: 0.875, borderRadius: '4px', bgcolor: 'grey.100', color: 'text.secondary', fontSize: '0.6875rem', fontWeight: 600 }}
    >
      {t('productBrowser.variantGroup.hasVariants')}
    </Box>
  );

  // Unknown group, or a group of one: nothing to select, keep today's tag.
  if (!state || state.total < 2) return variant === 'chip' ? hasVariantsTag : null;

  if (state.allSelected) {
    return variant === 'chip' ? (
      <Box
        sx={{ display: 'inline-flex', alignItems: 'center', height: 20, px: 0.875, borderRadius: '4px', bgcolor: 'grey.100', color: 'text.secondary', fontSize: '0.6875rem', fontWeight: 600 }}
      >
        {t('productBrowser.variantGroup.allSelected')}
      </Box>
    ) : (
      <Box sx={{ fontSize: '12.5px', fontWeight: 600, color: 'text.secondary' }}>
        {t('productBrowser.variantGroup.allSelected')}
      </Box>
    );
  }

  const label = addMutation.isPending
    ? t('productBrowser.variantGroup.adding')
    : t('productBrowser.variantGroup.selectAll', { count: state.count });
  const run = (event: React.MouseEvent) => {
    event.stopPropagation();
    addMutation.mutate(state.unselectedIds);
  };

  if (variant === 'chip') {
    return (
      <Box
        component="button"
        type="button"
        onClick={run}
        disabled={addMutation.isPending}
        sx={{
          appearance: 'none', display: 'inline-flex', alignItems: 'center', height: 20, px: 0.875, borderRadius: '4px',
          border: 'none', bgcolor: 'grey.100', color: 'primary.main', fontSize: '0.6875rem', fontWeight: 600,
          font: 'inherit', cursor: 'pointer', '&:hover': { bgcolor: 'grey.200' }, '&:disabled': { cursor: 'default', opacity: 0.7 },
        }}
      >
        {label}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-start' }}>
      <Button
        onClick={run}
        disabled={addMutation.isPending}
        startIcon={addMutation.isPending ? <CircularProgress size={14} /> : undefined}
        sx={{ height: 30, px: '12px', borderRadius: '8px', border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', color: 'text.primary', textTransform: 'none', fontWeight: 600, fontSize: '12.5px' }}
      >
        {label}
      </Button>
      {capError && <SyncCapNotice error={capError} onDismiss={() => setCapError(null)} />}
    </Box>
  );
}
